import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Lixeira = ({
  API,
  getAuthHeaders,
  setCurrentView,
  loadTurmas,
  loadStudents,
  loadRevistas
}) => {
  const [activeTab, setActiveTab] = useState('turmas');
  const [turmasDesativadas, setTurmasDesativadas] = useState([]);
  const [alunosDesativados, setAlunosDesativados] = useState([]);
  const [revistasDesativadas, setRevistasDesativadas] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadItensDesativados();
  }, []);

  const loadItensDesativados = async () => {
    setLoading(true);
    try {
      const [turmasRes, alunosRes, revistasRes] = await Promise.all([
        axios.get(`${API}/turmas/desativadas/listar`, { headers: getAuthHeaders() }),
        axios.get(`${API}/students/desativados/listar`, { headers: getAuthHeaders() }),
        axios.get(`${API}/revistas/desativadas/listar`, { headers: getAuthHeaders() })
      ]);
      setTurmasDesativadas(turmasRes.data);
      setAlunosDesativados(alunosRes.data);
      setRevistasDesativadas(revistasRes.data);
    } catch (error) {
      alert('❌ Erro ao carregar itens da lixeira');
    } finally {
      setLoading(false);
    }
  };

  const restaurarTurma = async (turmaId) => {
    if (window.confirm('Restaurar esta turma?')) {
      try {
        await axios.put(`${API}/turmas/${turmaId}/restaurar`, {}, { headers: getAuthHeaders() });
        alert('✅ Turma restaurada com sucesso!');
        await loadItensDesativados();
        await loadTurmas(true); // Atualizar lista de turmas ativas
      } catch (error) {
        alert('❌ Erro ao restaurar turma');
      }
    }
  };

  const restaurarAluno = async (alunoId) => {
    if (window.confirm('Restaurar este aluno?')) {
      try {
        await axios.put(`${API}/students/${alunoId}/restaurar`, {}, { headers: getAuthHeaders() });
        alert('✅ Aluno restaurado com sucesso!');
        await loadItensDesativados();
        await loadStudents(true); // Atualizar lista de alunos ativos
      } catch (error) {
        alert('❌ Erro ao restaurar aluno');
      }
    }
  };

  const restaurarRevista = async (revistaId) => {
    if (window.confirm('Restaurar esta revista?')) {
      try {
        await axios.put(`${API}/revistas/${revistaId}/restaurar`, {}, { headers: getAuthHeaders() });
        alert('✅ Revista restaurada com sucesso!');
        await loadItensDesativados();
        await loadRevistas(true); // Atualizar lista de revistas ativas
      } catch (error) {
        alert('❌ Erro ao restaurar revista');
      }
    }
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => setCurrentView('dashboard')}
            className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            ← Voltar ao Dashboard
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">🗑️ Lixeira</h1>
          <p className="text-gray-600">Restaure itens que foram removidos acidentalmente</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('turmas')}
              className={`flex-1 px-4 py-3 text-sm md:text-base font-medium ${
                activeTab === 'turmas'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              🎓 Turmas ({turmasDesativadas.length})
            </button>
            <button
              onClick={() => setActiveTab('alunos')}
              className={`flex-1 px-4 py-3 text-sm md:text-base font-medium ${
                activeTab === 'alunos'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              👨‍🎓 Alunos ({alunosDesativados.length})
            </button>
            <button
              onClick={() => setActiveTab('revistas')}
              className={`flex-1 px-4 py-3 text-sm md:text-base font-medium ${
                activeTab === 'revistas'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              📖 Revistas ({revistasDesativadas.length})
            </button>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin text-4xl mb-4">⟳</div>
                <p className="text-gray-600">Carregando...</p>
              </div>
            ) : (
              <>
                {activeTab === 'turmas' && (
                  <div>
                    {turmasDesativadas.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>📭 Nenhuma turma na lixeira</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {turmasDesativadas.map(turma => (
                          <div key={turma.id} className="border rounded-lg p-4 flex justify-between items-center">
                            <div>
                              <h4 className="font-medium text-gray-800">{turma.nome}</h4>
                              <p className="text-sm text-gray-600">
                                Professor: {turma.professor}
                                {turma.desativada_em && (
                                  <span className="ml-2 text-gray-500">
                                    • Removida em {new Date(turma.desativada_em).toLocaleDateString('pt-BR')}
                                  </span>
                                )}
                              </p>
                            </div>
                            <button
                              onClick={() => restaurarTurma(turma.id)}
                              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                            >
                              ↺ Restaurar
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'alunos' && (
                  <div>
                    {alunosDesativados.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>📭 Nenhum aluno na lixeira</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {alunosDesativados.map(aluno => (
                          <div key={aluno.id} className="border rounded-lg p-4 flex justify-between items-center">
                            <div>
                              <h4 className="font-medium text-gray-800">{aluno.nome}</h4>
                              <p className="text-sm text-gray-600">
                                Turma: {aluno.turma_nome}
                                {aluno.desativado_em && (
                                  <span className="ml-2 text-gray-500">
                                    • Removido em {new Date(aluno.desativado_em).toLocaleDateString('pt-BR')}
                                  </span>
                                )}
                              </p>
                            </div>
                            <button
                              onClick={() => restaurarAluno(aluno.id)}
                              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                            >
                              ↺ Restaurar
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'revistas' && (
                  <div>
                    {revistasDesativadas.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>📭 Nenhuma revista na lixeira</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {revistasDesativadas.map(revista => (
                          <div key={revista.id} className="border rounded-lg p-4 flex justify-between items-center">
                            <div>
                              <h4 className="font-medium text-gray-800">{revista.tema}</h4>
                              <p className="text-sm text-gray-600">
                                {revista.total_licoes} lições • R$ {revista.valor.toFixed(2)}
                                {revista.desativada_em && (
                                  <span className="ml-2 text-gray-500">
                                    • Removida em {new Date(revista.desativada_em).toLocaleDateString('pt-BR')}
                                  </span>
                                )}
                              </p>
                            </div>
                            <button
                              onClick={() => restaurarRevista(revista.id)}
                              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                            >
                              ↺ Restaurar
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="bg-blue-50 rounded-lg p-6">
          <h4 className="font-semibold text-blue-800 mb-3">ℹ️ Sobre a Lixeira:</h4>
          <ul className="text-sm text-blue-700 space-y-2">
            <li>• Itens removidos não são deletados permanentemente</li>
            <li>• Você pode restaurar turmas, alunos e revistas a qualquer momento</li>
            <li>• Apenas administradores e moderadores podem acessar a lixeira</li>
            <li>• Ao restaurar, o item volta a aparecer nas listagens normais</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Lixeira;
